/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import classnames from 'classnames';
import onClickOutside from 'react-onclickoutside';
import partial from 'lodash/partial';

// TODO Review use of this class by reading https://lets-do-this.atlassian.net/browse/LDS-42
import { GLOBAL_FORM_STYLES_CLASS } from '@/globalClasses';

import { usePrevious } from '@hooks/usePrevious';
import history from '@/routes/routes/helpers/history';
import Icon, { Icons } from '../../Icons/Icon';
import Loading from '../../Loading';
import SearchIcon from '../../Icons/Search';
import TagSearchResults from './components/TagSearchResults';
import ResultListComponent from './components/ResultList';
import SeeAllLink from './components/SeeAllLink';
import NearMeItem from './components/NearMeItem';

import {
  clearButton,
  fullWidth,
  inPortal as inPortalStyle,
  inputFocused,
  panelActive,
  queryInput,
  queryWrapper,
  searchIcon,
  searchResults,
  searchResultsVisible,
  shadow,
  whiteBackground,
  closeArrow,
} from './styles.module.css';

// TODO:(tomo@) A lot of the code here needs cleaning up, it's
//              become pretty hard to follow what's actually
//              happening.
const GlobalSearch = ({
  autoFocus,
  className,
  countryCode,
  customEventList = null,
  defaultTags,
  disableClickOutside,
  disableLinkOut,
  disableOnEnter,
  events: { total, events },
  fetchData,
  fullBleed,
  hideLocation,
  hideNearMe,
  hideSearchIcon = false,
  hideSeeAll,
  hideTags,
  inPortal,
  keepOpen,
  locations,
  noShadow,
  onClose,
  onEventSelect,
  panelClass,
  placeholder = 'Search events or places',
  queryWrapperClassOverride,
  routes,
  searchResultsClassOverride,
  showClearButton,
  tags,
  trackEventSearchClicked,
  trackInputEntered,
  trackLocationSearchClicked,
  trackNearMeSearchClicked,
  trackPanelClosed,
  trackPanelOpened,
  trackShowAllClicked,
  trackTagSearchClicked,
  whiteBg,
}) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(autoFocus || false);
  const previousFocused = usePrevious(focused);
  const [showLoadingState, setShowLoadingState] = useState(false);
  const [firstItemUrl, setFirstItemUrl] = useState();

  // Handler for react-onclickoutside
  GlobalSearch.handleClickOutside = (e) => {
    if (disableClickOutside) return;
    setFocused(false);
  };

  useEffect(() => {
    fetchData(query);
    if (!query?.length) setShowLoadingState(false);
  }, [query]);

  useEffect(() => {
    setShowLoadingState(false);
  }, [events, tags, locations]);

  const handleClose = (name) => {
    trackPanelClosed(name || 'closed', { countryCode, query });
    setFocused(false);
  };

  useEffect(() => {
    if (previousFocused === true && focused === false) {
      handleClose('click_out');
    }
  }, [focused]);

  const handleCloseClick = () => {
    if (typeof onClose === 'function') {
      onClose();
    }

    handleClose();
  };

  const handleEnter = (e) => {
    if (disableOnEnter) return;
    if (e.key === 'Enter' && firstItemUrl) {
      trackInputEntered('on_enter_key', { countryCode, query });
      if (onEventSelect) {
        onEventSelect(e);
      } else {
        history.push(firstItemUrl);
      }
      handleCloseClick();
      setQuery('');
    }
  };

  const handleInput = ({ target }) => {
    if (target?.value) {
      setShowLoadingState(true);
    }
    setQuery(target?.value);
  };

  const handleFocus = (name) => {
    setFocused(true);
    trackPanelOpened(name || 'input_focused', {});
  };

  const useDiscoveryHeader = routes?.slice?.(-1)?.pop?.()?.useDiscoveryHeader;
  const hideBrowseTagsSection = (useDiscoveryHeader && inPortal) || hideTags;

  const ResultList = customEventList ?? ResultListComponent;
  return (
    <div
      className={classnames(className, GLOBAL_FORM_STYLES_CLASS, {
        [whiteBackground]: whiteBg,
        [fullWidth]: fullBleed,
      })}
    >
      <div
        className={classnames(queryWrapper, {
          [shadow]: !noShadow && focused,
          [panelActive]: focused,
          [queryWrapperClassOverride]: queryWrapperClassOverride,
        })}
      >
        {inPortal && (
          <Icon
            className={closeArrow}
            icon={Icons.ARROW_LEFT.BLACK}
            onClick={handleCloseClick}
            onKeyUp={handleCloseClick}
          />
        )}
        {!inPortal && !hideSearchIcon && (
          <SearchIcon
            onClick={partial(handleFocus, 'search_icon_clicked')}
            className={searchIcon}
          />
        )}
        <input
          onClick={partial(handleFocus, 'input_clicked')}
          value={query}
          onChange={handleInput}
          onKeyDown={handleEnter}
          className={classnames(queryInput, {
            [inputFocused]: focused,
          })}
          onFocus={partial(handleFocus, 'input_on_focused')}
          placeholder={placeholder}
          data-hj-whitelist
          autoFocus={focused}
          autoComplete="off"
          autoCorrect="off"
        />
        {(showClearButton || (inPortal && query)) && (
          <span
            className={clearButton}
            onClick={() => setQuery('')}
            onKeyUp={() => setQuery('')}
          >
            Clear
          </span>
        )}
      </div>
      {(focused || keepOpen) && (
        <div
          className={classnames(searchResults, panelClass, {
            [shadow]: !noShadow,
            [searchResultsVisible]: focused || keepOpen,
            [fullWidth]: fullBleed,
            [inPortalStyle]: inPortal,
            [searchResultsClassOverride]: searchResultsClassOverride,
          })}
        >
          {showLoadingState && <Loading />}
          {!showLoadingState && query && (
            <>
              <ResultList
                disableLinkOut={disableLinkOut}
                tags={tags}
                events={events}
                locations={locations}
                hideLocation={hideLocation}
                hideTags={hideTags}
                setFirstItemUrl={setFirstItemUrl}
                onEventClick={(params) => {
                  if (onEventSelect) {
                    onEventSelect(params);
                  }
                  handleCloseClick();
                  setQuery('');
                  trackEventSearchClicked({
                    ...params,
                    events,
                    query,
                    countryCode,
                    total,
                  });
                }}
                onTagClick={(params) => {
                  handleCloseClick();
                  setQuery('');
                  trackTagSearchClicked({
                    ...params,
                    tags,
                    query,
                    countryCode,
                  });
                }}
                onLocationClick={(params) => {
                  handleCloseClick();
                  setQuery('');
                  trackLocationSearchClicked({
                    ...params,
                    locations,
                    query,
                    countryCode,
                  });
                }}
              />
              {!hideSeeAll && (
                <SeeAllLink
                  totalEventCount={total}
                  query={query}
                  countryCode={countryCode}
                  onClick={() => {
                    handleCloseClick();
                    setQuery('');
                    trackShowAllClicked();
                  }}
                />
              )}
            </>
          )}
          {!showLoadingState && !query && (
            <>
              {!hideNearMe && (
                <NearMeItem
                  countryCode={countryCode}
                  onClick={() => {
                    handleCloseClick();
                    setQuery('');
                    trackNearMeSearchClicked();
                  }}
                />
              )}
              {!hideBrowseTagsSection && (
                <TagSearchResults
                  tags={defaultTags}
                  query={query}
                  onClick={() => {
                    handleCloseClick();
                    setQuery('');
                    trackTagSearchClicked();
                  }}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const clickOutsideConfig = {
  handleClickOutside: () => GlobalSearch.handleClickOutside,
};

export default withRouter(onClickOutside(GlobalSearch, clickOutsideConfig));
